import { dashboardDb } from './dashboardDb';
import { getAllCareerNotesForExport, bulkPutCareerNotes, } from './careerFeedDb';
import { getAllNotesForExport, bulkPutFeedNotes, } from './enrichFeedDb';
import { ROADMAP_REFLECTION_QUESTIONS } from '../shared/DeckSpaceModal';
import { PATHWAY_STEPS, getStepInputFields } from '../../constants/ModalContext';
import { loadCoachFeedNotes, getAllCoachFeedNotesForExport, bulkPutCoachFeedNotes, } from './coachFeedDb';
import { loadLeaderFeedNotes, getAllLeaderFeedNotesForExport, bulkPutLeaderFeedNotes, } from './leaderFeedDb';
import { getCoachDailyContentCache, getLeaderDailyContentCache } from './dashboardDb';

// ── LEGEND: what each assessmentType means (context for the local LLM) ──
export const ASSESSMENT_TYPE_CONTEXT = {
  Subject: 'UniReady Part II — core subject-interest assessment; scores rank which academic subjects the student is most drawn to.',
  SubjectPart3: 'UniReady Part III — deeper subject-exploration assessment, only offered for a student\'s top-ranked Part II subjects.',
  Career: 'FutureReady — career interests/personality, working values, intrinsic vs extrinsic motivation, Big Five traits, emotional intelligence.',
  Learning: 'StudyStyle — learning style across sensory modality, social/environmental orientation, self-regulation, motivation, cognitive style.',
};

// ── Score aggregation (mirrors Dashboard.js's own computePartIIScores /
// computePartIIIScores exactly, so the export never disagrees with the UI) ──
function computePartIIScores(answers) {
  const facetMap = answers.reduce((acc, a) => {
    const facet = a.facet || 'Unknown';
    if (!acc[facet]) acc[facet] = { scores: [], taxonomy: new Set(), facetNote: a.facet_note || null };
    acc[facet].scores.push(a.score);
    if (Array.isArray(a.taxonomy)) a.taxonomy.forEach(t => acc[facet].taxonomy.add(t));
    return acc;
  }, {});
  return Object.entries(facetMap).filter(([, d]) => d.scores.length > 0).map(([facet, d]) => ({
    facet,
    averageScore: Number((d.scores.reduce((s, v) => s + v, 0) / d.scores.length).toFixed(2)),
    taxonomy: Array.from(d.taxonomy),
    facetNote: d.facetNote,
  }));
}

function computePartIIIScores(answers) {
  const facetMap = answers.reduce((acc, a) => {
    const facet = a.facet || 'Unknown';
    if (!acc[facet]) acc[facet] = { scores: [], facetNote: a.facet_note || null };
    acc[facet].scores.push(a.score);
    return acc;
  }, {});
  return Object.entries(facetMap).filter(([, d]) => d.scores.length > 0).map(([facet, d]) => ({
    facet,
    averageScore: Number((d.scores.reduce((s, v) => s + v, 0) / d.scores.length).toFixed(2)),
    facetNote: d.facetNote,
  }));
}

// Career/Learning: grouped by sub_category (Career) or category (Learning) —
// same grouping Dashboard.js uses to rebuild visualizationsCache when it's missing.
function computeGroupedFacetScores(answers, groupKeyField) {
  const byGroup = answers.reduce((acc, a) => {
    const group = a[groupKeyField] || 'Unknown';
    if (!acc[group]) acc[group] = [];
    acc[group].push(a);
    return acc;
  }, {});
  const out = {};
  for (const [group, groupAnswers] of Object.entries(byGroup)) {
    const facetMap = groupAnswers.reduce((acc, a) => {
      const facet = a.facet || 'Unknown';
      if (!acc[facet]) acc[facet] = { scores: [], facetNote: a.facet_note || null };
      acc[facet].scores.push(a.score);
      return acc;
    }, {});
    out[group] = Object.entries(facetMap).map(([facet, d]) => ({
      facet,
      averageScore: Number((d.scores.reduce((s, v) => s + v, 0) / d.scores.length).toFixed(2)),
      facetNote: d.facetNote,
    }));
  }
  return out;
}


function summarizePathwayProgress(records) {
  return records.map(row => {
    const pathways = {};
    Object.entries(row.data || {}).forEach(([pathwayKey, entry]) => {
      const stepLabels = Object.fromEntries((PATHWAY_STEPS[pathwayKey] || []).map(s => [s.key, s.label]));
      const completedArray = entry.completed instanceof Set
        ? Array.from(entry.completed)
        : Array.isArray(entry.completed) ? entry.completed : [];

      const reflections = Object.entries(entry.reflections || {}).map(([stepKey, fields]) => {
        const fieldConfigs = getStepInputFields(pathwayKey, stepKey);
        const labelByField = Object.fromEntries(fieldConfigs.map(f => [f.field, f.label]));
        return {
          step: stepLabels[stepKey] || stepKey,
          answers: Object.entries(fields).map(([field, value]) => ({
            question: labelByField[field] || field,
            value,
          })),
        };
      });

      pathways[pathwayKey] = {
        goal: entry.goals || null,
        completedSteps: completedArray.map(k => stepLabels[k] || k),
        reflections,
      };
    });
    return { userId: row.userId, pathways };
  });
}

function summarizeRoadmapReflections(records) {
  const promptByKey = Object.fromEntries(ROADMAP_REFLECTION_QUESTIONS.map(q => [q.key, q.prompt]));
  return records.map(row => ({
    userId: row.userId,
    roadmapType: row.roadmapType,
    updatedAt: row.updatedAt,
    reflections: Object.entries(row.data || {}).map(([key, value]) => ({
      question: promptByKey[key] || key, // falls back to raw key if the question list ever drifts
      value,
    })),
  }));
}

// rawAnswers table -> compact per-facet scores, never raw item-level responses
function summarizeRawAnswers(records) {
  return records.map(r => {
    const { userId, type, sessionId, timestamp, data = [] } = r;
    let scores;
    switch (type) {
      case 'Subject':
        scores = computePartIIScores(data.filter(a => a.category === 'Part II'));
        break;
      case 'SubjectPart3':
        scores = computePartIIIScores(data.filter(a => a.category === 'Part III'));
        break;
      case 'Career':
        scores = computeGroupedFacetScores(data, 'sub_category');
        break;
      case 'Learning':
        scores = computeGroupedFacetScores(data, 'category');
        break;
      default:
        scores = [];
    }
    return {
      userId, assessmentType: type,
      assessmentContext: ASSESSMENT_TYPE_CONTEXT[type] || null,
      sessionId, computedAt: timestamp, scores,
    };
  });
}

// ── Shared feed-note summarizer factory ─────────────────────────────────────
// CoachFeed, LeaderFeed, AcadFeed, and ProFeed notes all share the same
// shape: JSON-or-legacy-string, an articleMeta stamp resolving title/
// snippet/link/source, and the same takeaway/tag/savedForLater/updatedAt
// fields. Previously each had its own ~25-line copy of this logic, which is
// exactly how CoachFeed/LeaderFeed ended up silently missing from the
// manifest for a while — a shared factory means any future feed table gets
// the same fields (including `unresolved`) by construction, not by
// remembering to copy every field correctly.
//
// metaCategoryField lets the caller pick which articleMeta field maps to
// the output's `category` key (CoachFeed/LeaderFeed use `category_name`,
// AcadFeed uses `subject`, ProFeed uses `career`).
function makeFeedNoteSummarizer(metaCategoryField) {
  return function summarizeFeedNotes(rowsOrMap) {
    // Accept either a { guid: rawText } map (Coach/Leader) or an array of
    // { guid, text } rows (AcadFeed/ProFeed) — normalize to entries first.
    const entries = Array.isArray(rowsOrMap)
      ? rowsOrMap.map(row => [row.guid, row.text])
      : Object.entries(rowsOrMap || {});

    return entries.map(([guid, raw]) => {
      let note;
      try {
        note = JSON.parse(raw);
      } catch {
        note = { takeaway: raw }; // legacy plain-text note, pre-JSON format
      }
      const meta = note.articleMeta || null;
      return {
        guid,
        title: meta?.title ?? null,
        category: meta?.[metaCategoryField] ?? null,
        snippet: meta?.snippet ?? null,
        link: meta?.link ?? null,
        source: meta?.source ?? null,
        unresolved: !meta,
        takeaway: note.takeaway || '',
        tag: note.tag || '',
        savedForLater: !!note.savedForLater,
        updatedAt: note.updatedAt || null,
      };
    });
  };
}

const summarizeCoachFeedNotes = makeFeedNoteSummarizer('category_name');
const summarizeLeaderFeedNotes = makeFeedNoteSummarizer('category_name');
const summarizeAcadFeedNotesShared = makeFeedNoteSummarizer('subject');
const summarizeCareerNotesShared = makeFeedNoteSummarizer('career');

// ── Shared: the common note-content tail every structured note type ends
// with. Doesn't replace the per-table meta-resolution logic (each of
// resourceNotes/aiCourseNotes/coachXcelNotes/leaderXcelNotes resolves
// different meta fields), just the repeated trailing shape so a future
// note type can't accidentally drop `unresolved` or a field name.
function noteContentFields(note) {
  return {
    takeaway: note.takeaway,
    unclear: note.unclear,
    tag: note.tag,
    confidence: note.confidence,
    usefulness: note.usefulness,
    updatedAt: note.updatedAt,
  };
}

async function resolveXperienceProjectMeta(db, userId) {
  const row = await db.xperienceProjectsIndex.get(userId);
  const map = {};
  (row?.data || []).forEach(p => {
    map[p.id] = { name: p.name, stage: p.stage, ideaSnippet: p.ideaSnippet };
  });
  return map;
}

// ── Shared: every StartupXperience project-scoped table (journal resources,
// connections, advice history, traction metrics) is exported the same way —
// resolve the project name via xperienceProjectsIndex, then re-key the raw
// data array under a table-specific field name.
//
// IMPORTANT: this must be defined ABOVE EXPORT_MANIFEST, not below it.
// EXPORT_MANIFEST is a top-level array literal evaluated immediately at
// module load, and its entries reference these names directly as values
// (transform: summarizeXperienceJournalResourcesAsync). Unlike `function`
// declarations, `const` bindings don't hoist — if this block sits below
// EXPORT_MANIFEST in the file, referencing them above throws a
// "Cannot access before initialization" ReferenceError at load time.
function makeXperienceProjectScopedSummarizer(fieldName) {
  return async function summarize(records, db, userId) {
    const projectMeta = await resolveXperienceProjectMeta(db, userId);
    return records.map(row => ({
      userId: row.userId,
      projectId: row.projectId,
      projectName: projectMeta[row.projectId]?.name ?? null,
      [fieldName]: row.data || [],
    }));
  };
}

const summarizeXperienceJournalResourcesAsync = makeXperienceProjectScopedSummarizer('resources'); // [{id, title, link, addedAt}]
const summarizeXperienceConnectionsAsync = makeXperienceProjectScopedSummarizer('connections'); // [{id, name, link, industry, position, notes, addedAt}]
const summarizeXperienceAdviceHistoryAsync = makeXperienceProjectScopedSummarizer('adviceEntries'); // [{id, stage, taskGoal, prompt, response, timestamp}]
const summarizeXperienceTractionMetricsAsync = makeXperienceProjectScopedSummarizer('metrics'); // [{id, name, unit, entries:[{id,value,date}], createdAt}]


// visualizationsCache table -> flatten chart.js dataset shape into plain facet scores
function summarizeVisualizations(records) {
  return records
    .filter(r => !['career', 'learning'].includes((r.kind || '').toLowerCase()))
    .map(r => {
      const { userId, kind, data = {}, timestamp } = r;
      const groups = {};
      for (const [groupKey, radar] of Object.entries(data)) {
        const labels = radar?.labels || [];
        const values = radar?.datasets?.[0]?.data || [];
        const notes = radar?.facetNote || {};
        groups[groupKey] = labels.map((facet, i) => ({
          facet,
          averageScore: values[i] ?? null,
          facetNote: notes[facet] ?? null,
        }));
      }
      return {
        userId, assessmentType: kind,
        assessmentContext: ASSESSMENT_TYPE_CONTEXT[kind] || null,
        computedAt: timestamp, groups,
      };
    });
}

// ── MANIFEST ──────────────────────────────────────────────
export const EXPORT_MANIFEST = [
  // ---- psychometric_result (now scores only, never raw item-level answers) ----
  { table: 'rawAnswers', domain: 'UniReady/FutureReady/StudyStyle', category: 'psychometric_result',
    description: 'Aggregated per-facet average scores for each assessment (Subject/SubjectPart3/Career/Learning). Item-level responses are intentionally excluded — only the derived facet averages are exported.',
    transform: summarizeRawAnswers },
{ table: 'visualizationsCache', domain: 'FutureReady/StudyStyle', category: 'psychometric_result',
  description: 'Cached facet-average scores for any assessment kind not already covered by rawAnswers. Career and Learning are intentionally excluded here — those are exported once, via rawAnswers, to avoid shipping the same facet scores twice from two independently-written sources.',
  transform: summarizeVisualizations },
 { table: 'coachAssessmentCache', domain: 'CoachPlus', category: 'psychometric_result',
  description: "Student's CoachReady competency assessment results, aggregated to per-facet average scores. Item-level responses are intentionally excluded, matching rawAnswers.",
  transform: summarizeAssessmentCache },
{ table: 'leaderAssessmentCache', domain: 'LeaderPlus', category: 'psychometric_result',
  description: "Student's LeaderReady competency assessment results, aggregated to per-facet average scores. Item-level responses are intentionally excluded, matching rawAnswers.",
  transform: summarizeAssessmentCache },

  // ---- raw_input (typed/authored directly by the student) ----
  { table: 'subjectReasons', domain: 'UniReady', category: 'raw_input',
    description: "Student's own stated reasoning for their subject preferences (tags + free text), per subject." },
  { table: 'planNotes', domain: 'SuperCurricular', category: 'raw_input',
    description: 'Activity Journal: goal, why it matters, status, dated progress log, outcome reflection, and up to 3 self-tagged skills developed — one entry per super-curricular activity, resolved with the activity\'s subject, title, details, and impact via activityMeta.',
    transform: summarizePlanNotes },
  { table: 'resourceNotes', domain: 'Knowledge', category: 'raw_input',
    description: "Student's reflection on a Knowledge-page resource, per subject: key takeaway, what to explore next, tag, understanding level, usefulness rating. resourceMeta gives title/author/type once saved after the source patch; unresolved: true means it predates that patch.",
    transform: summarizeResourceNotes },
  { table: 'personalStatementAnswers', domain: 'UniStatement', category: 'raw_input',
    description: 'Personal statement / essay draft answers.' },
  { table: 'userResources', domain: 'Knowledge', category: 'raw_input',
    description: 'Resources the student manually added, per subject.' },
  { table: 'starNotes', domain: 'PortfolioX', category: 'raw_input',
    description: 'STAR-method story (Situation/Task/Action/Result), per job + skill.' },
  { table: 'starNotesBySkill', domain: 'PortfolioX', category: 'raw_input',
    description: 'STAR-method story shared across all jobs, keyed by skill only (current canonical version). `progressPercent` and `associatedJobpostIds` resolve the shared skill-progress ring and every career this skill note applies to.',
    transform: summarizeStarNotesAsync },  { table: 'interviewAnswers', domain: 'InterviewPro', category: 'raw_input',
    description: "Student's own answers to mock interview questions." },
  { table: 'addonSections', domain: 'PortfolioX', category: 'raw_input',
    description: 'Free-text skills/qualifications/higher-ed entries the student added to their portfolio.' },
  { table: 'higherEducation', domain: 'PortfolioX', category: 'raw_input',
    description: 'Higher education entries the student listed.' },
  { table: 'starExperiences', domain: 'PortfolioX', category: 'raw_input',
    description: 'List of STAR experiences the student has logged.' },
  { table: 'challengeReflections', domain: 'TeamChallenge', category: 'raw_input',
    description: "Student's structured reflections after a team challenge." },
  { table: 'aiSubskillJournal', domain: 'LeaderPlus/AIXcel', category: 'raw_input',
    description: 'Development journal entries per AI sub-skill: {text, progress, timestamp}.' },
  { table: 'aiCourseNotes', domain: 'LeaderPlus/AIXcel', category: 'raw_input',
    description: "Student's reflection on an AIXcel free course: key takeaway, what to explore next, tag, understanding level, usefulness rating. `courseMeta` gives facet/title/type/source once saved after this patch; notes predating it surface with unresolved: true.",
    transform: (records) => records.map(r => ({
      userId: r.userId,
      notes: Object.entries(r.data || {}).map(([rowId, note]) => ({
        rowId,
        facet: note.courseMeta?.facet ?? null,
        courseTitle: note.courseMeta?.courseTitle ?? null,
        courseType: note.courseMeta?.courseType ?? null,
        level: note.courseMeta?.level ?? null,
        source: note.courseMeta?.source ?? null,
unresolved: !note.courseMeta,
        ...noteContentFields(note),
      })),
    })) },
  { table: 'launchpadProjectNotes', domain: 'LaunchPad', category: 'raw_input',
    description: 'Free-text notes on a LaunchPad venture project.' },
  { table: 'xperienceJournalResources', domain: 'StartupXperience', category: 'raw_input',
    description: 'Reference Library resources saved to a startup project, with the project name resolved from xperienceProjectsIndex.',
    transform: summarizeXperienceJournalResourcesAsync },
  { table: 'xperienceTractionMetrics', domain: 'StartupXperience', category: 'raw_input',
    description: 'Traction metrics (e.g. signups, revenue) the student is tracking for a startup project, with dated data points and project name resolved from xperienceProjectsIndex.',
    transform: summarizeXperienceTractionMetricsAsync },
 { table: 'coachXcelNotes', domain: 'CoachPlus/CoachXcel', category: 'raw_input',
  description: "Student's general and per-resource coaching notes. `skillMeta`/`resourceMeta` give skill name / resource title-author-type-link once saved after this patch; unresolved: true means the note predates it.",
  transform: (records) => records.map(r => ({
    userId: r.userId,
    notes: Object.entries(r.data || {}).flatMap(([key, value]) => {
      if (key.startsWith('general_')) {
        return [{
          rowId: key,
          scope: 'general',
          skillName: value.skillMeta?.skillName ?? key.replace('general_', ''),
unresolved: !value.skillMeta,
          ...noteContentFields(value),
        }];
      }
      if (key.startsWith('resources_')) {
        const skillName = key.replace('resources_', '');
        return Object.entries(value || {}).map(([resourceId, note]) => ({
          rowId: `${key}_${resourceId}`,
          scope: 'resource',
          skillName,
          resourceId,
          resourceTitle: note.resourceMeta?.title ?? null,
          resourceAuthor: note.resourceMeta?.author ?? null,
          resourceType: note.resourceMeta?.type ?? null,
          resourceLink: note.resourceMeta?.link ?? null,
         unresolved: !note.resourceMeta,
          ...noteContentFields(note),
        }));
      }
      return []; // ignore any unrecognised key shape rather than throwing
    }),
  })) },
 { table: 'leaderXcelNotes', domain: 'LeaderPlus/LeaderXcel', category: 'raw_input',
  description: "Student's general and per-resource leadership notes. `skillMeta`/`resourceMeta` give skill name / resource title-author-type-link once saved after this patch; unresolved: true means the note predates it.",
  transform: (records) => records.map(r => ({
    userId: r.userId,
    notes: Object.entries(r.data || {}).flatMap(([key, value]) => {
      if (key.startsWith('general_')) {
        return [{
          rowId: key,
          scope: 'general',
          skillName: value.skillMeta?.skillName ?? key.replace('general_', ''),
          unresolved: !value.skillMeta,
          ...noteContentFields(value),
        }];
      }
      if (key.startsWith('resources_')) {
        const skillName = key.replace('resources_', '');
        return Object.entries(value || {}).map(([resourceId, note]) => ({
          rowId: `${key}_${resourceId}`,
          scope: 'resource',
          skillName,
          resourceId,
          resourceTitle: note.resourceMeta?.title ?? null,
          resourceAuthor: note.resourceMeta?.author ?? null,
          resourceType: note.resourceMeta?.type ?? null,
          resourceLink: note.resourceMeta?.link ?? null,
          unresolved: !note.resourceMeta,
          ...noteContentFields(note),
        }));
      }
      return [];
    }),
  })) },

{ table: 'coachDailyProgress', domain: 'CoachingDailyPractice', category: 'raw_input',
  description: 'Full daily-practice progress store: streak, reflections, completed days, per-slot answers. Generated content (drill/mission/reflection prompt) is trimmed to identifying labels and instructional text, not the full scenario/question payload — the student\'s own drillText/reflection/missionJournal carry the signal. In-progress (uncompleted) slots under `today` are joined against coachDailyContentCache at export time so answers stay paired with what they respond to — `todaySlots[].contentResolved: false` means that join found nothing.',
  transform: summarizeCoachDailyProgressAsync },
  { table: 'interviewCustomQuestions', domain: 'InterviewPro', category: 'raw_input',
    description: 'Custom interview questions the student added themselves, per job.' },
  { table: 'roadmapReflections', domain: 'Roadmap', category: 'raw_input',
 description: 'Student feedback that builds the learner intelligence profile behind roadmap generation — each answer paired with the question it responds to (e.g. "Which milestone feels most challenging?").',
    transform: summarizeRoadmapReflections },

  // ---- llm_generated ----

  { table: 'insightsCache', domain: 'Insights', category: 'llm_generated',
    description: 'AI-generated career/child insights digest.' },
  { table: 'strategyCache', domain: 'Strategy', category: 'llm_generated',
    description: 'AI-generated strategy content (self view or parent view).' },
  { table: 'roadmaps', domain: 'Roadmap', category: 'llm_generated',
    description: 'AI-generated personalised roadmap content, e.g. the 3-year career plan. Note: schema bug — primary key is userId alone, so multiple roadmap "type" values may overwrite each other on save; worth a compound-key fix before relying on multiple types per user.' },
  { table: 'launchpadProjectAdvisorNotes', domain: 'LaunchPad', category: 'llm_generated',
    description: 'AI Venture Advisor output for a project: nextSteps, marketingPlan, riskCompliance, each with a student takeaway/next-action/confidence reflection. `projectName` resolves the deck this belongs to.',
    transform: summarizeAdvisorNotesAsync },  
  { table: 'xperienceAdviceHistory', domain: 'StartupXperience', category: 'llm_generated',
    description: 'History of AI advice given for a startup project — each entry includes the stage, task goal, generated prompt, and the response the founder pasted back. Project name resolved from xperienceProjectsIndex.',
    transform: summarizeXperienceAdviceHistoryAsync },
{ table: 'learnerIntelligence', domain: 'Roadmap', category: 'raw_input',
  description: 'Student-authored reflection on career direction, concerns, values, and goals — self-reported, not model-generated. `associatedInsights` gives the generationDate of the currently cached AI insights this reflection is responding to (full text lives in the insightsCache section, not duplicated here).',
  transform: summarizeLearnerIntelligenceAsync },
{ table: 'studyLearnerIntelligence', domain: 'StudyStyle', category: 'raw_input',
  description: 'Student-authored study self-assessment (strategy-fit/confidence ratings, challenges, goal, preferred activities, environment, current stage), with `associatedStrategy` giving the generationDate of the currently cached AI-generated strategy this reflection responds to (full text lives in the strategyCache section, not duplicated here).',
  transform: summarizeStudyLearnerIntelligenceAsync },

    // ---- user_content (derived-but-owned state) ----
  { table: 'chosenTopSubject', domain: 'UniReady', category: 'user_content',
    description: 'A subject the student manually declared as their top choice, bypassing the assessment (used when they already know their focus, e.g. already applied/studying it).' },
  { table: 'pastChallenges', domain: 'TeamChallenge', category: 'user_content',
    description: "Student's history of completed team challenges." },
  { table: 'optionCompletions', domain: 'SuperCurricular', category: 'user_content',
    description: 'Checkbox completion state for suggested super-curricular options.' },
  { table: 'activityStatuses', domain: 'SuperCurricular', category: 'user_content',
    description: 'not_started/in_progress/completed status per activity.' },
  { table: 'knowledgeCheckboxes', domain: 'Knowledge', category: 'user_content',
    description: '"Completed" checkbox state per subject on the Knowledge page.' },
  { table: 'psStatusMap', domain: 'UniStatement', category: 'user_content',
    description: 'Personal statement completion status per subject.' },
  { table: 'skillProgress', domain: 'PortfolioX', category: 'user_content',
    description: 'Skill mastery ring value, per job + skill.' },
  { table: 'skillProgressBySkill', domain: 'PortfolioX', category: 'user_content',
    description: 'Skill mastery ring value, shared across jobs (current canonical version).' },
  { table: 'portfolioJobs', domain: 'PortfolioX', category: 'user_content',
    description: 'Jobs the student added to their career portfolio.' },
  { table: 'extraCareerJobs', domain: 'Compass', category: 'user_content',
    description: 'Extra career options the student explored on Compass.' },
  { table: 'interviewJobs', domain: 'InterviewPro', category: 'user_content',
    description: 'Jobs the student is preparing interviews for.' },
  { table: 'interviewStatuses', domain: 'InterviewPro', category: 'user_content',
    description: 'Interview prep status per job.' },
  { table: 'portfolioStatus', domain: 'PortfolioX', category: 'user_content',
    description: 'Overall portfolio completion status.' },
  { table: 'calendarEvents', domain: 'Shared', category: 'user_content',
    description: 'All calendar events, tagged by source feature (e.g. jobtracker-).' },
  { table: 'pathwayProgress', domain: 'Pathways', category: 'user_content',
description: "Student's declared goal, completed-step checklist, and per-step reflections for each pathway (CAREERReady, LEADAI, ASPIRELab, etc.), with step and question labels resolved from PATHWAY_STEPS/getStepInputFields.",
    transform: summarizePathwayProgress },  { table: 'userSettings', domain: 'Shared', category: 'user_content',
    description: 'Student profile settings: grade/year level, country.' },
  { table: 'aiSkillProgress', domain: 'LeaderPlus/AIXcel', category: 'user_content',
    description: 'AI sub-skill mastery ring values.' },
  { table: 'launchpadProjectsIndex', domain: 'LaunchPad', category: 'user_content',
    description: "Index of the student's LaunchPad venture projects." },
  { table: 'launchpadProjectData', domain: 'LaunchPad', category: 'user_content',
    description: 'Core deck-section content for a venture project (the actual business idea/plan data).' },
  { table: 'launchpadProjectVcs', domain: 'LaunchPad', category: 'user_content',
    description: 'VC/grant shortlist the student built for a project.' },
  { table: 'seedFundLocation', domain: 'LaunchPad', category: 'user_content',
    description: 'Region filter used for Seed Fund search.' },
  { table: 'xperienceProjectsIndex', domain: 'StartupXperience', category: 'user_content',
    description: "Index of the student's StartupXperience projects." },
  { table: 'xperienceProjectData', domain: 'StartupXperience', category: 'user_content',
    description: 'Core project state: the idea, its history, and current task.' },
  { table: 'xperienceConnections', domain: 'StartupXperience', category: 'user_content',
    description: "Founder's Network connections added to a project, with the project name resolved from xperienceProjectsIndex.",
    transform: summarizeXperienceConnectionsAsync },
  { table: 'xperienceWatchedResources', domain: 'StartupXperience', category: 'user_content',
    description: 'Which video resources the student has watched (global across projects).' },
  { table: 'coachCompletedResources', domain: 'CoachPlus', category: 'user_content',
    description: 'Completed-resource checkmarks, per skill.' },
  { table: 'coachDailySeenCompetencies', domain: 'CoachingDailyPractice', category: 'user_content',
    description: 'Competencies already covered, to avoid repeats across days.' },
  { table: 'coachDailySelectedDifficulty', domain: 'CoachingDailyPractice', category: 'user_content',
    description: "Student's selected difficulty level for daily practice." },
  { table: 'leaderCompletedResources', domain: 'LeaderPlus', category: 'user_content',
    description: 'Completed-resource checkmarks, per skill.' },
  { table: 'jobTrackerEntries', domain: 'PortfolioX', category: 'user_content',
    description: 'Job application tracker entries: stage, key dates, notes.' },

{ table: 'leaderDailySeenCompetencies', domain: 'LeaderPilot', category: 'user_content',
  description: 'Leadership competencies already covered, to avoid repeats across days.' },
{ table: 'leaderDailySelectedDifficulty', domain: 'LeaderPilot', category: 'user_content',
  description: "Student's selected difficulty level for leadership daily practice." },
{ table: 'leaderDailyProgress', domain: 'LeaderPilot', category: 'raw_input',
  description: 'Full leadership daily-practice progress store: streak, reflections, completed days, per-slot answers. Generated content (drill/mission/reflection prompt) is trimmed to identifying labels and instructional text, not the full scenario/question payload — the student\'s own drillText/reflection/missionJournal carry the signal. In-progress (uncompleted) slots under `today` are joined against leaderDailyContentCache at export time so answers stay paired with what they respond to — `todaySlots[].contentResolved: false` means that join found nothing.',
  transform: summarizeLeaderDailyProgressAsync },
  ];

// ── EXPORT ENGINE (updated) ──────────────────────────────
export async function exportUserData(userId, { since = null } = {}) {
  const bundle = {
    exportVersion: 3, // v3: resolved cross-references for planNotes, resourceNotes, study reflections
    userId,
    generatedAt: new Date().toISOString(),
    since,
    legend: ASSESSMENT_TYPE_CONTEXT,
    sections: [],
  };

  for (const entry of EXPORT_MANIFEST) {
    const table = dashboardDb[entry.table];
    if (!table) continue;

    let records = await table.where('userId').equals(userId).toArray();

if (since) {
  const cursor = new Date(since).getTime();
  records = records.filter(r => {
    const ts = r.updatedAt ?? r.timestamp ?? r.savedAt ?? r._savedAt; // NEW: _savedAt fallback
    return ts == null || new Date(ts).getTime() >= cursor;
  });
}

    if (records.length === 0) continue;

    if (entry.transform) {
      try {
        records = await entry.transform(records, dashboardDb, userId); // now async, gets db for joins
      } catch (err) {
        console.warn(`Export transform failed for ${entry.table}:`, err.message);
        continue;
      }
    }

    if (!records || records.length === 0) continue;

    bundle.sections.push({
      domain: entry.domain,
      table: entry.table,
      category: entry.category,
      description: entry.description,
      recordCount: records.length,
      records,
    });
  }

  // ── Feed notes each live in a separate Dexie database (CareerFeedDB,
  // EnrichFeedDB, CoachFeedDB, leaderFeedDB) outside dashboardDb, so they
  // can't go through the generic EXPORT_MANIFEST loop above — but the
  // fetch→summarize→conditionally-push shape is identical across all four,
  // so it's data-driven here instead of four hand-copied blocks.
  const FEED_SOURCES = [
    {
      domain: 'ProFeed', table: 'careerNotes',
      description: "Student's takeaway note and tag on a career-field news article, with the article's title/career/snippet/link resolved from articleMeta once the note is saved after this patch. Notes pre-dating the patch surface with unresolved: true.",
      fetch: () => summarizeCareerNotes(userId),
    },
    {
      domain: 'AcadFeed', table: 'notes',
      description: "Student's takeaway note and tag on a subject-specific academic news article, with title/subject/snippet/link resolved from articleMeta once the note is saved after this patch. Notes pre-dating the patch surface with unresolved: true.",
      fetch: async () => summarizeAcadFeedNotesShared(await getAllNotesForExport(userId)),
    },
    {
      domain: 'CoachFeed', table: 'coachFeedNotes',
      description: "Student's takeaway note and tag on a coaching/mentoring news article, with title/category/snippet/link resolved from articleMeta once the note is saved after this patch. Notes pre-dating the patch surface with unresolved: true.",
      fetch: async () => summarizeCoachFeedNotes(await loadCoachFeedNotes(userId)),
    },
    {
      domain: 'LeaderFeed', table: 'leaderFeedNotes',
      description: "Student's takeaway note and tag on a leadership/strategy news article, with title/category/snippet/link resolved from articleMeta once the note is saved after this patch. Notes pre-dating the patch surface with unresolved: true.",
      fetch: async () => summarizeLeaderFeedNotes(await loadLeaderFeedNotes(userId)),
    },
  ];

  for (const src of FEED_SOURCES) {
    const records = await src.fetch();
    if (records.length > 0) {
      bundle.sections.push({
        domain: src.domain,
        table: src.table,
        category: 'raw_input',
        description: src.description,
        recordCount: records.length,
        records,
      });
    }
  }

  return bundle;
}

// ── Trim a generated daily-practice content payload down to identifying
// labels + instructional text. The full items[]/reflectionQuestions[]
// arrays are AI-generated scaffolding re-embedded on every completed day —
// keeping them at full fidelity means N days of history re-ships near-
// duplicate drill scaffolding N times. The student's own words (drillText,
// reflection, missionJournal — already top-level on the entry/slot) are
// the actual signal; this keeps just enough of the prompt for context.
function trimDailyContentForExport(content) {
  if (!content) return null;
  return {
    competency: content.competency ?? null,
    mindset: content.mindset?.mindset ?? content.mindset ?? null,
    drillType: content.drill?.drillType ?? null,
    drillTitle: content.drill?.title ?? null,
    drillInstructions: content.drill?.instructions ?? null,
    missionType: content.mission?.missionType ?? null,
    missionText: content.mission?.mission ?? null,
    reflectionType: content.reflectionPrompt?.reflectionType ?? null,
    reflectionPromptText: content.reflectionPrompt?.reflection ?? null,
  };
}

// ── Shared helper: join in-progress "today" slots against a daily-content-cache
// table. Completed days already self-embed their content snapshot at
// completeDay() time (see entry.content in pastReflections), so only
// uncompleted `today` slots need this join — the generated drill/mission/
// reflection prompt otherwise lives only in a separate, TTL-pruned cache
// table and is never persisted alongside the student's answer.
async function summarizeDailyProgressAsync(records, getContentCacheFn) {
  return Promise.all(records.map(async (row) => {
    const todaySlots = await Promise.all(
      Object.entries(row.today || {}).map(async ([slotKey, slot]) => {
        const lastUnderscoreIdx = slotKey.lastIndexOf('_');
        const dateKey = slotKey.slice(0, lastUnderscoreIdx);
        const difficulty = slotKey.slice(lastUnderscoreIdx + 1);

        let content = null;
        if (!slot.missionDone) {
          try {
            const cached = await getContentCacheFn(row.userId, dateKey, difficulty);
            content = cached?.data ? trimDailyContentForExport(cached.data) : null;
          } catch {
            content = null;
          }
        }

        return { slotKey, dateKey, difficulty, ...slot, content, contentResolved: !!content };
      })
    );

    const pastReflections = Array.isArray(row.pastReflections)
      ? row.pastReflections.map(entry => ({
          ...entry,
          content: entry.content ? trimDailyContentForExport(entry.content) : entry.content,
        }))
      : row.pastReflections;

    // CHANGED: destructure `today` out instead of spreading + overwriting
    // with undefined, which leaves the key present (with value undefined)
    // on the object — only vanishing later if something happens to run this
    // through JSON.stringify. Destructuring actually removes it.
    const { today, ...rowWithoutToday } = row;
    return { ...rowWithoutToday, todaySlots, pastReflections };
  }));
}

async function summarizeCoachDailyProgressAsync(records) {
  return summarizeDailyProgressAsync(records, getCoachDailyContentCache);
}

async function summarizeLeaderDailyProgressAsync(records) {
  return summarizeDailyProgressAsync(records, getLeaderDailyContentCache);
}


function summarizePlanNotes(records) {
  return records.map((row) => {
    const entries = Object.entries(row.data || {}).map(([activityId, entry]) => ({
      activityId,
      subject: entry.activityMeta?.subject ?? null,
      activityTitle: entry.activityMeta?.activityTitle ?? null,
      activityDetails: entry.activityMeta?.activityDetails ?? null,
      impact: entry.activityMeta?.impact ?? null,
      category: entry.activityMeta?.category ?? null,
      unresolved: !entry.activityMeta, // true only for entries created before this patch
      goal: entry.goal, whyItMatters: entry.whyItMatters, status: entry.status,
      logEntries: entry.logEntries, outcomeReflection: entry.outcomeReflection,
      skillsDeveloped: entry.skillsDeveloped, updatedAt: entry.updatedAt,
    }));
    return { userId: row.userId, updatedAt: row.updatedAt, journalEntries: entries };
  });
}

function summarizeResourceNotes(records) {
  return records.map((row) => {
    const entries = Object.entries(row.data || {}).map(([rowId, note]) => ({
      rowId,
      subject: note.resourceMeta?.subject ?? row.subject ?? null,
      resourceTitle: note.resourceMeta?.resourceTitle ?? null,
      author: note.resourceMeta?.author ?? null,
      resourceType: note.resourceMeta?.resourceType ?? null,
      details: note.resourceMeta?.details ?? null,
isUserAdded: note.resourceMeta?.isUserAdded ?? null,
      unresolved: !note.resourceMeta, // true only for notes saved before this patch
      ...noteContentFields(note),
      createdAt: note.createdAt,
    }));
    return { userId: row.userId, subject: row.subject, notes: entries };
  });
}

// ── Coach/Leader assessment caches: same treatment as rawAnswers — aggregate
// to per-facet averages, never ship item-level answers. resultData.answers
// is the same raw item shape UniReady/FutureReady/StudyStyle assessments use
// (per-item facet/score/category/facet_note), just stored under a different
// cache table (coachAssessmentCache/leaderAssessmentCache instead of
// rawAnswers) because Coach/Leader assessments don't go through a
// visualizationsCache-style aggregation step upstream.
function aggregateAssessmentCacheAnswers(answers = []) {
  const facetMap = answers.reduce((acc, a) => {
    const facet = a.facet || 'Unknown';
    if (!acc[facet]) acc[facet] = { scores: [], facetNote: a.facet_note || null, category: a.category || null };
    acc[facet].scores.push(a.score);
    return acc;
  }, {});
  return Object.entries(facetMap)
    .filter(([, d]) => d.scores.length > 0)
    .map(([facet, d]) => ({
      facet,
      category: d.category,
      averageScore: Number((d.scores.reduce((s, v) => s + v, 0) / d.scores.length).toFixed(2)),
      facetNote: d.facetNote,
    }));
}

function summarizeAssessmentCache(records) {
  return records.map(r => {
    const data = r.data || {};
    return {
      userId: r.userId,
      sessionId: data.sessionId ?? null,
      lastCompletionDate: data.lastCompletionDate ?? null,
      computedAt: r.timestamp,
      scores: aggregateAssessmentCacheAnswers(data.answers),
    };
  });
}



async function resolveProjectNames(db, userId) {
  const projects = await db.launchpadProjectsIndex.get(userId);
  const map = {};
  (projects?.data || []).forEach(p => { map[p.id] = p.name; });
  return map;
}

async function summarizeAdvisorNotesAsync(records, db, userId) {
  const projectNames = await resolveProjectNames(db, userId);
  return records.map(r => ({ ...r, projectName: projectNames[r.projectId] ?? null }));
}

// ── ProFeed notes (separate Dexie database: CareerFeedDB) ──────────────────
async function summarizeCareerNotes(userId) {
  const rows = await getAllCareerNotesForExport(userId); // [{userId, guid, text}]
  return summarizeCareerNotesShared(rows);
}

// ── studyLearnerIntelligence: attach the currently cached self strategy ──
async function summarizeStudyLearnerIntelligenceAsync(records, db, userId) {
  const strategyRow = await db.strategyCache.get(`${userId}_self`);
  return records.map((row) => ({
    ...row,
    associatedStrategy: strategyRow
      ? { generationDate: strategyRow.generationDate, seeSection: 'strategyCache' }
      : null, // no strategy generated yet, or reflection predates it
  }));
};

async function summarizeLearnerIntelligenceAsync(records, db, userId) {
  const insightsRow = await db.insightsCache.get(`${userId}_career`);
  return records.map((row) => ({
    ...row,
    associatedInsights: insightsRow
      ? { generationDate: insightsRow.generationDate, seeSection: 'insightsCache' }
      : null,
  }));
};

async function summarizeStarNotesAsync(records, db, userId) {
  const [progressRows, indexRows] = await Promise.all([
    db.skillProgressBySkill.where('userId').equals(userId).toArray(),
    db.starIndex.get(userId),
  ]);
  const progressBySkill = {};
  progressRows.forEach(r => { progressBySkill[r.skillName.trim().toLowerCase()] = r.value; });

  const jobsBySkill = {};
  (indexRows?.data || []).forEach(({ jobpostId, skillName }) => {
    const norm = skillName.trim().toLowerCase();
    if (!jobsBySkill[norm]) jobsBySkill[norm] = new Set();
    jobsBySkill[norm].add(jobpostId);
  });

  return records.map((row) => ({
    ...row,
    progressPercent: progressBySkill[row.skillName.trim().toLowerCase()] ?? null,
    associatedJobpostIds: [...(jobsBySkill[row.skillName.trim().toLowerCase()] || [])],
  }));
};



// Reuse the same table list the LLM-context export already walks
const BACKUP_TABLES = EXPORT_MANIFEST.map(e => e.table);

// Raw, untransformed dump — safe to restore from exactly
export async function backupUserData(userId) {
  const backup = {
    backupVersion: 2, // v2: adds feeds{} alongside tables{}
    userId,
    generatedAt: new Date().toISOString(),
    tables: {},
    feeds: {},
  };

  for (const tableName of BACKUP_TABLES) {
    const table = dashboardDb[tableName];
    if (!table) continue;
    const records = await table.where('userId').equals(userId).toArray();
    if (records.length > 0) backup.tables[tableName] = records;
  }

// ── Feed notes: separate Dexie databases, each independent so one
  // failing doesn't block the rest of the backup ──
  const BACKUP_FEED_SOURCES = [
    { key: 'careerNotes', label: 'careerNotes (ProFeed)', fetch: getAllCareerNotesForExport },
    { key: 'acadFeedNotes', label: 'acadFeedNotes (AcadFeed)', fetch: getAllNotesForExport },
    { key: 'coachFeedNotes', label: 'coachFeedNotes', fetch: getAllCoachFeedNotesForExport },
    { key: 'leaderFeedNotes', label: 'leaderFeedNotes', fetch: getAllLeaderFeedNotesForExport },
  ];

  for (const src of BACKUP_FEED_SOURCES) {
    try {
      const rows = await src.fetch(userId);
      if (rows.length > 0) backup.feeds[src.key] = rows;
    } catch (err) { console.warn(`Backup: ${src.label} failed`, err.message); }
  }

  return backup;
}

export async function restoreUserData(backup) {
  if (!backup || (!backup.tables && !backup.feeds)) {
    throw new Error('Invalid backup file.');
  }

  let restoredCount = 0;
  let tableCount = 0;

  // ── dashboardDb tables ──
  for (const [tableName, records] of Object.entries(backup.tables || {})) {
    const table = dashboardDb[tableName];
    if (!table || !Array.isArray(records) || records.length === 0) continue;
    await table.bulkPut(records); // upsert by primary key — safe to re-run
    restoredCount += records.length;
    tableCount += 1;
  }

// ── Feed notes — each wrapped so one failure doesn't abort the rest ──
  const feeds = backup.feeds || {};

  const RESTORE_FEED_SOURCES = [
    { key: 'careerNotes', label: 'careerNotes', bulkPut: bulkPutCareerNotes },
    { key: 'acadFeedNotes', label: 'acadFeedNotes', bulkPut: bulkPutFeedNotes },
    { key: 'coachFeedNotes', label: 'coachFeedNotes', bulkPut: bulkPutCoachFeedNotes },
    { key: 'leaderFeedNotes', label: 'leaderFeedNotes', bulkPut: bulkPutLeaderFeedNotes },
  ];

  for (const src of RESTORE_FEED_SOURCES) {
    const rows = feeds[src.key];
    if (!Array.isArray(rows) || rows.length === 0) continue;
    try {
      await src.bulkPut(rows);
      restoredCount += rows.length;
      tableCount += 1;
    } catch (err) { console.warn(`Restore: ${src.label} failed`, err.message); }
  }

  return { restoredCount, tableCount };
}